import { HttpClient } from '@angular/common/http';
import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom, fromEvent, interval } from 'rxjs';

export interface VersionManifest {
  version: string;
  buildNumber: number;
}

const POLL_INTERVAL_MS = 2 * 60 * 1000;
const VERSION_URL = '/assets/version.json';

@Injectable({ providedIn: 'root' })
export class AppVersionService {
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);

  readonly newVersionAvailable = signal(false);

  private baselineBuildNumber: number | null = null;

  async initialize(): Promise<void> {
    try {
      const manifest = await this.fetchManifest();
      this.baselineBuildNumber = manifest.buildNumber;
    } catch {
      // Without a baseline, checkForUpdate() short-circuits — banner stays hidden.
    }

    interval(POLL_INTERVAL_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        void this.checkForUpdate();
      });

    if (typeof document !== 'undefined') {
      fromEvent(document, 'visibilitychange')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          if (document.visibilityState === 'visible') {
            void this.checkForUpdate();
          }
        });
    }
  }

  private async checkForUpdate(): Promise<void> {
    // Once latched, do not re-check — the banner stays until the user reloads.
    if (this.newVersionAvailable()) {
      return;
    }

    try {
      const manifest = await this.fetchManifest();
      // If init failed, adopt the first successful fetch as the baseline.
      // We miss this deploy but catch all future ones (vs. never detecting any).
      if (this.baselineBuildNumber === null) {
        this.baselineBuildNumber = manifest.buildNumber;
        return;
      }
      if (manifest.buildNumber !== this.baselineBuildNumber) {
        this.newVersionAvailable.set(true);
      }
    } catch {
      // Transient failures are ignored; next poll will retry.
    }
  }

  private fetchManifest(): Promise<VersionManifest> {
    return firstValueFrom(
      this.http.get<VersionManifest>(`${VERSION_URL}?t=${Date.now()}`, {
        headers: { 'Cache-Control': 'no-cache' },
      }),
    );
  }
}
